/**
 * Route prefetch helpers
 *
 * Lightweight on-demand chunk prefetching for the most-visited routes.
 * Triggered by hover/focus on nav links and an idle timer after first paint.
 *
 * No service worker, no caching layer — just nudges the browser to start
 * downloading the lazy chunk before the user clicks.
 */

type Prefetcher = () => Promise<unknown>;

const prefetchers: Record<string, Prefetcher> = {
  "/menu": () => import("../pages/MenuPage.tsx"),
  "/about": () => import("../pages/AboutPage.tsx"),
  "/contact": () => import("../pages/ContactPage.tsx"),
  "/reservations": () => import("../pages/ReservationsPage.tsx"),
};

const started = new Set<string>();

export function prefetchRoute(path: string): void {
  const fn = prefetchers[path];
  if (!fn || started.has(path)) return;
  started.add(path);
  // Fire-and-forget; swallow errors so prefetch never breaks the app.
  fn().catch(() => {
    started.delete(path);
  });
}

/**
 * Schedule prefetch of the most-likely next routes after the browser is idle.
 * Call once after first paint (e.g. from main.tsx).
 */
export function schedulePrefetch(paths: string[] = ["/menu", "/reservations"]): void {
  if (typeof window === "undefined") return;
  const run = () => paths.forEach(prefetchRoute);
  if ("requestIdleCallback" in window) {
    (window as Window & {
      requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 2500);
  }
}
